import { EntityUtils } from '@record-it-npm/synchro-client';
import React, { ComponentProps, FunctionComponent, useCallback } from 'react';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import {
  SortableList,
  SortableListProps
} from 'record-it-components/list/SortableList/SortableList';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { SortUtils } from 'record-it-utils/utils/SortUtils/SortUtils';
import { TimeTrackingActivity } from '../../../classes/entities/TimeTrackingActivity/timeTrackingActivityTypes';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { TimeTrackingActivityListItem } from '../TimeTrackingActivityListItem/TimeTrackingActivityListItem';

export type TimeTrackingActivityListProps = PropsWithoutChildren<
  ComponentProps<'div'>
> & {
  timeTrackingActivities: Array<TimeTrackingActivity>;
};

export const TimeTrackingActivityList: FunctionComponent<TimeTrackingActivityListProps> =
  React.memo((props) => {
    const { timeTrackingActivities, ...remainingProps } = prepareProps({
      props,
      ignoreChildren: true
    });

    const timeTrackingActivityRepository = useEntityRepository(
      EntityName.TimeTrackingActivity
    );

    const getItemKey: SortableListProps<TimeTrackingActivity>['getItemKey'] =
      useCallback((activity) => {
        return EntityUtils.getTrackingKey(activity);
      }, []);

    const renderItem: SortableListProps<TimeTrackingActivity>['renderItem'] =
      useCallback(({ item, ref, style }) => {
        return (
          <TimeTrackingActivityListItem
            ref={ref}
            timeTrackingActivity={item}
            style={style}
            data-testid="TimeTrackingActivityList.ListItem"
          ></TimeTrackingActivityListItem>
        );
      }, []);

    const handleItemSorted: SortableListProps<TimeTrackingActivity>['onItemSorted'] =
      useEventCallback(({ item, previousItem }) => {
        const sortedActivities = SortUtils.sortIntoArrayAfterItem(
          timeTrackingActivities,
          item,
          previousItem
        );

        for (const [index, activity] of sortedActivities.entries()) {
          if (activity.order !== index) {
            timeTrackingActivityRepository.update({
              ...activity,
              order: index
            });
          }
        }
      });

    return (
      <div {...remainingProps}>
        <SortableList
          items={timeTrackingActivities}
          renderItem={renderItem}
          getItemKey={getItemKey}
          onItemSorted={handleItemSorted}
        ></SortableList>
      </div>
    );
  });
